import { NgbTime } from '@shared/components/ngb-timepicker/ngb-time';
import { Constants, toInteger } from '.';
import { UtcDateTimeOptions } from '@shared/enums';
import { DateFilter } from '@shared/models';

const MS_PER_DAY = 1000 * 60 * 60 * 24;
const MS_PER_SECOND = 1000;
const MS_PER_MINUTE = 60 * MS_PER_SECOND;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;
const MS_PER_400_YEARS = (365 * 400 + 97) * 24 * MS_PER_HOUR;

export const DprDetailsTimeFormat = 'ccc, MMM d';
export const DprLockDateFormat = 'dd-MM-yyyy';

/**
 * @description Converts the given time to a string of format "HH:mm"
 * If end of day then return 24:00
 * @param time The string time
 * @returns The string representation of the time in "HH:mm" format
 */
export function convertTimeToDisplayFormat(time: string): string {
  if (!time) {
    return null;
  }

  const dateTime = convertTimeToDate(time);

  if (isDefaultFinishTime(dateTime)) {
    return '24:00';
  }

  return convertToDisplayFormat(dateTime.getHours(), dateTime.getMinutes());
}

/**
 * @description Converts the given time into a date
 * @param stringTime The string time
 * @returns The date representation of the time
 */
export function convertTimeToDate(stringTime: string): Date {
  // expected format HH:mm:ss
  const timeRegex = /^([0-1]?[0-9]|2[0-4]):([0-5][0-9])(?::([0-5][0-9]))?$/;

  if (!stringTime?.match(timeRegex)) {
    return null;
  }

  const parts = stringTime.split(':');
  return sanitizeTime(
    toInteger(parts[0]),
    toInteger(parts[1]),
    toInteger(parts[2])
  );
}

function sanitizeTime(hours: number, minutes: number, seconds?: number): Date {
  const date = new Date(0);
  date.setHours(hours);
  date.setMinutes(minutes);
  date.setSeconds(seconds || 0);

  return date;
}

export function getCurrentTimeAsString(): string {
  const currentDateTime = new Date();
  return convertDateToTimeString(
    sanitizeTime(currentDateTime.getHours(), currentDateTime.getMinutes())
  );
}

export function isDefaultStartTime(time: Date): boolean {
  return compareTime(time, defaultStartTime()) === 0;
}

export function isDefaultFinishTime(time: Date): boolean {
  return compareTime(time, getDefaultFinishTime()) === 0;
}

export function defaultStartTime(): Date {
  return sanitizeTime(0, 0);
}

function getDefaultFinishTime(): Date {
  return sanitizeTime(23, 59, 59);
}

export function compareTime(firstTime: Date, secondTime: Date): number {
  if (!firstTime || !secondTime) {
    return null;
  }

  const first = sanitizeTime(
    firstTime.getHours(),
    firstTime.getMinutes(),
    firstTime.getSeconds()
  );
  const second = sanitizeTime(
    secondTime.getHours(),
    secondTime.getMinutes(),
    secondTime.getSeconds()
  );

  if (first > second) {
    return 1;
  }

  if (second > first) {
    return -1;
  }

  return 0;
}

export function convertStringTimeToNgbTime(stringTime: string): NgbTime {
  // expected format HH:mm:ss
  const timeRegex = /^(\d{2}):(\d{2}):(\d{2})$/;

  if (!stringTime?.match(timeRegex)) {
    return null;
  }

  const parts = stringTime.split(':');
  return new NgbTime(
    toInteger(parts[0]),
    toInteger(parts[1]),
    toInteger(parts[2])
  );
}

export function isStartOfDay(time: NgbTime): boolean {
  if (!time) {
    return false;
  }
  return time.hour === 0 && time.minute === 0 && time.second === 0;
}

export function isEndOfDay(time: NgbTime): boolean {
  if (!time) {
    return false;
  }
  return time.hour === 23 && time.minute === 59 && time.second === 59;
}

export function convertToDisplayFormat(hours: number, minutes: number): string {
  let result = '';
  if (hours > 9) {
    result += hours;
  } else {
    result += '0' + hours;
  }

  result += ':';

  if (minutes > 9) {
    result += minutes;
  } else {
    result += '0' + minutes;
  }

  return result;
}

export function toUTCDate(
  date: Date | number | string,
  options: UtcDateTimeOptions
): Date {
  let result: Date;

  const d = new Date(date);

  switch (options) {
    case UtcDateTimeOptions.UtcDateWithTime:
      result = new Date(
        d.getUTCFullYear(),
        d.getUTCMonth(),
        d.getUTCDate(),
        d.getUTCHours(),
        d.getUTCMinutes()
      );
      break;
    case UtcDateTimeOptions.UtcDateWithoutTime:
      result = new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
      break;
    case UtcDateTimeOptions.UtcDateWithoutTimeZone:
      result = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
      break;
    default:
      break;
  }

  return result;
}

export function convertToDateWithoutTimeZoneSuffix(d: Date | string): string {
  const date = new Date(d);

  let month: string;

  if (date.getMonth() === 0) {
    month = date.getMonth() + '1';
  } else if (date.getMonth() > 0 && date.getMonth() < 9) {
    month = '0' + (date.getMonth() + 1);
  } else {
    month = (date.getMonth() + 1).toString();
  }

  return (
    date.getFullYear() +
    '-' +
    month +
    '-' +
    (date.getDate() < 10 ? '0' + date.getDate() : date.getDate())
  );
}

export function convertDateToJsonDateFormat(date?: Date): string {
  if (!date) {
    date = new Date();
  }
  const month = date.getMonth() + 1;
  const day = date.getDate();
  return (
    date.getFullYear() +
    '-' +
    ('0' + month).slice(-2) +
    '-' +
    (day <= 9 ? '0' + day : day) +
    'T00:00:00Z'
  );
}

export function convertDateToTimeString(time: Date): string {
  if (!time) {
    return null;
  }

  const hh = time.getHours();
  const mm = time.getMinutes();
  const ss = time.getSeconds();

  return [
    (hh > 9 ? '' : '0') + hh,
    (mm > 9 ? '' : '0') + mm,
    (ss > 9 ? '' : '0') + ss,
  ].join(':');
}

export function computeDiffTime(finishTime: Date, startTime: Date): string {
  let minutes = computeDiffTimeAsMinutes(finishTime, startTime);
  const hours = Math.floor(minutes / 60);
  minutes = minutes - hours * 60;

  return convertToDisplayFormat(hours, minutes);
}

export function computeDiffTimeAsHours(
  finishTime: Date,
  startTime: Date
): number {
  let minutes = computeDiffTimeAsMinutes(finishTime, startTime);
  const hours = Math.floor(minutes / 60);

  minutes = minutes - hours * 60;

  const minutesInHours = minutes / 60;

  return hours + minutesInHours;
}

export function computeDiffTimeAsMinutes(
  finishTime: Date,
  startTime: Date
): number {
  if (!finishTime || !startTime) {
    return null;
  }

  if (compareTime(startTime, finishTime) > 0) {
    return null;
  }

  const finish = sanitizeTime(
    finishTime.getHours(),
    finishTime.getMinutes(),
    finishTime.getSeconds()
  );
  const start = sanitizeTime(
    startTime.getHours(),
    startTime.getMinutes(),
    startTime.getSeconds()
  );

  if (isDefaultFinishTime(finish)) {
    // next day at 00:00
    finish.setSeconds(60);
  }

  if (isDefaultFinishTime(start)) {
    // next day at 00:00
    start.setSeconds(60);
  }

  return Math.floor((finish.getTime() - start.getTime()) / 1000 / 60);
}

export function convertDateToString(date: Date): string {
  if (!date) {
    return null;
  }

  const month = date.getMonth() + 1;
  return date.getFullYear() + '-' + month + '-' + date.getDate();
}

export function convertDateToDisplayFormat(date: Date): string {
  if (!date) {
    return null;
  }

  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = (date.getFullYear() % 100).toString().padStart(2, '0');

  return day + '-' + month + '-' + year;
}

// TODO: replace addDaysToDate usage with utcDateAdapter.addCalendarDays(), then remove this function
export function addDaysToDate(date: Date, numberOfDays: number): Date {
  return new Date(date.getTime() + numberOfDays * 24 * 60 * 60 * 1000);
}

export function dateDiffInDays(first: Date, second: Date): number {
  // discard the time and time-zone information
  const utc1 = Date.UTC(first.getFullYear(), first.getMonth(), first.getDate());
  const utc2 = Date.UTC(
    second.getFullYear(),
    second.getMonth(),
    second.getDate()
  );

  return Math.floor((utc2 - utc1) / MS_PER_DAY);
}

function utcStartOfDate(y: number, m: number, d: number): number {
  // Date.UTC remaps years 0-99 to 1900-1999
  if (y < 100 && y >= 0) {
    // preserve leap years using a full 400 year cycle, then reset
    return Date.UTC(y + 400, m, d) - MS_PER_400_YEARS;
  } else {
    return Date.UTC(y, m, d);
  }
}

export function startOf(date: Date, units: string): Date {
  if (!units || !date) {
    return date;
  }

  let time = date.getTime();

  switch (units) {
    case 'year':
      time = utcStartOfDate(date.getFullYear(), 0, 1);
      break;
    case 'month':
      time = utcStartOfDate(date.getFullYear(), date.getMonth(), 1);
      break;
    case 'week':
      time = utcStartOfDate(
        date.getFullYear(),
        date.getMonth(),
        date.getDate() - date.getDay()
      );
      break;
  }

  return new Date(time);
}

export function endOf(date: Date, units: string): Date {
  if (!units || !date) {
    return date;
  }

  let time = date.getTime();

  switch (units) {
    case 'year':
      time = utcStartOfDate(date.getFullYear() + 1, 0, 1);
      break;
    case 'month':
      time = utcStartOfDate(date.getFullYear(), date.getMonth() + 1, 1);
      break;
    case 'week':
      time = utcStartOfDate(
        date.getFullYear(),
        date.getMonth(),
        date.getDate() - date.getDay() + 7
      );
      break;
  }

  return subtract(new Date(time), 1, 'day');
}

export function subtract(date: Date, duration: number, units: string): Date {
  if (!units || !date || !duration) {
    return date;
  }

  const d = new Date(date);

  switch (units) {
    case 'year':
      d.setFullYear(d.getFullYear() - duration);
      break;
    case 'month':
      d.setMonth(d.getMonth() - duration);
      break;
    case 'day':
      d.setDate(d.getDate() - duration);
      break;
  }

  return d;
}

export function minutesToHoursAndMinutes(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;

  return (
    (h < 10 ? '0' + h.toString() : h.toString()) +
    ':' +
    (m < 10 ? '0' + m.toString() : m.toString())
  );
}

/**
 * converts the date string parameter to number in minutes
 *
 * @param hoursAndMinutes string parameter of a time value which is represented as hour and minutes (ex.: 02:01; 12:00; 24:00)
 * @returns a number which equals the number of minutes in the parameter (ex.: 01:00 -> 60; 24:00 -> 1440)
 */
export function hoursAndMinutesStringToNumber(hoursAndMinutes: string): number {
  const splittedDate = hoursAndMinutes.split(':');
  const minutes =
    parseInt(splittedDate[0], 10) * 60 + parseInt(splittedDate[1], 10);
  return minutes;
}

/**
 * Converts time string from "HH:mm" to "HH:mm:ss" format.
 *
 * @param timeString the time string in either "HH:mm" or "HH:mm:ss" format
 * @returns the time in "HH:mm:ss" format, or null if invalid
 */
export function normalizeTimeFormat(timeString: string): string | null {
  if (!timeString) {
    return null;
  }

  const match = timeString.match(
    /^([0-1]?[0-9]|2[0-4]):([0-5][0-9])(?::([0-5][0-9]))?$/
  );
  if (!match) {
    return null;
  }

  const [_, h, m, s = '0'] = match;
  const hour = toInteger(h);
  const minute = toInteger(m);
  const second = toInteger(s);

  // Convert 24:00:00 to 23:59:59
  if (hour === 24 && minute === 0 && second === 0) {
    return '23:59:59';
  }

  return new NgbTime(hour, minute, second).toString();
}

export function filterAvailableDates(
  date: Date | null,
  availableDates: DateFilter
): boolean {
  if (!availableDates) {
    return true;
  }

  const dates = availableDates.dates.map((availableDate) =>
    toUTCDate(availableDate, UtcDateTimeOptions.UtcDateWithoutTime).getTime()
  );

  const firstDate = toUTCDate(
    availableDates.firstDate,
    UtcDateTimeOptions.UtcDateWithoutTime
  ).getTime();
  const lastDate = toUTCDate(
    availableDates.lastDate,
    UtcDateTimeOptions.UtcDateWithoutTime
  ).getTime();

  return (
    dates.includes(date.getTime()) ||
    date.getTime() < firstDate ||
    date.getTime() > lastDate
  );
}

export function parseTimeString(
  value: string
): { hours: string; minutes: string } | null {
  const trimmed = value.trim();

  const twelve = trimmed.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (twelve) {
    return parseTwelveHourTimeString(twelve);
  }

  const twentyFour = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (twentyFour) {
    return parseTwentyFourHourTimeString(twentyFour);
  }

  return null;
}

function parseTwelveHourTimeString(
  twelve: RegExpMatchArray
): { hours: string; minutes: string } | null {
  if (twelve) {
    let h = Number(twelve[1]);
    const m = Number(twelve[2]);
    if (h < 1 || h > 12 || m > 59) return null;
    const period = twelve[3].toUpperCase();
    if (period === 'PM') {
      if (h !== 12) {
        h += 12;
      }
    }
    return { hours: h.toString(), minutes: twelve[2] };
  }

  return null;
}

function parseTwentyFourHourTimeString(
  twentyFour: RegExpMatchArray
): { hours: string; minutes: string } | null {
  if (twentyFour) {
    const h = Number(twentyFour[1]);
    const m = Number(twentyFour[2]);
    if (h > 24 || m > 59 || (h === 24 && m !== 0)) {
      return null;
    }

    return { hours: twentyFour[1], minutes: twentyFour[2] };
  }

  return null;
}

export function isDateBeforeEpoch(date: Date) {
  const minDate = toUTCDate(
    new Date(Constants.DateTime.MinConfigurableDate),
    UtcDateTimeOptions.UtcDateWithoutTime
  );
  return date < minDate;
}
